import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useEffect, useState, useRef } from 'react';
import { Modal, Button, Table } from 'react-bootstrap';

import { theme } from 'components/themes/v1';
import { readNotification, getPaginatedNotif, readAllNotification } from 'services/notification';
import { TARGET_TYPE_APPLICATION } from 'constants/notification';
import { storeUserUnreadNotif, storeUserPaginatedNotif, storeUserCurrentPage, storeUserLastPage, storeUserScrollPosition } from 'store/actions/notification';
import { DateToShortDateTime } from 'utils/datetime';
import NotificationModal from 'components/common/NotificationModal';

const Notification = styled.div`
  position: relative;
  width: 100%;
`;

const NotificationHeader = styled.div`
  font-size: 0.85rem;
  font-weight: bold;
  text-align: center;
  width: 100%;
  border-bottom: 1px solid ${(props) => props.theme.colors.divider};
  padding: 10px 8px;
`;

const NotificationBody = styled.div`
  width: 100%;
  margin-bottom: 14px;
  height: 300px;
  overflow-y: auto;
  position: relative;
`;

const NotificationFooter = styled.div`
  text-align: center;

  & a:hover {
    color: ${(props) => props.theme.colors.red};
  }
`;

const NotificationItem = styled.a`
  display: block;
  position: relative;
  cursor: pointer;
  text-decoration: none;
  font-size: 0.85rem;
  padding: 8px 12px;
  background-color: ${(props) => props.theme.colors.white};
  border-bottom: 1px solid ${(props) => props.theme.colors.divider};
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
    text-decoration: none;
    transition: all 0.3s ease-in-out;
  }
`;

const NotificationItemContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const NotificationItemTime = styled.span`
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.gray600};
  margin-top: 4px;
  align-self: flex-end;
`;

const NotificationItemBadge = styled.div`
  display: ${(props) => (props.show ? 'block' : 'none')};
  position: absolute;
  background-color: ${theme.colors.danger};
  width: 6px;
  height: 6px;
  border-radius: 50%;
  right: 10px;
  top: 10px;
`;

const NotificationModalTable = styled(Table)`
  margin-bottom: 0;
  font-size: 0.9rem;
  
  thead th {
    border-bottom: 2px solid ${(props) => props.theme.colors.divider};
    background-color: ${(props) => props.theme.colors.gray50};
    padding: 12px 8px;
    font-weight: 600;
  }
  
  tbody td {
    padding: 10px 8px;
    vertical-align: middle;
    border-bottom: 1px solid ${(props) => props.theme.colors.divider};
  }
`;

const NotificationBadgeSmall = styled.span`
  display: inline-block;
  background-color: ${theme.colors.danger};
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${theme.colors.primary};
  }
`;

const ModalPagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  padding: 0;
  
  button {
    padding: 2px 6px;
    border: 1px solid ${(props) => props.theme.colors.divider};
    background: white;
    cursor: pointer;
    border-radius: 4px;
    font-size: 0.85rem;
    
    &:hover {
      background: ${(props) => props.theme.colors.gray100};
    }
    
    &.active {
      background: ${theme.colors.primary};
      color: white;
      border-color: ${theme.colors.primary};
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const UserNotification = (props) => {
  const notifUnread = useSelector((state) => state.userNotif.unread);
  const reduxNotifData = useSelector((state) => state.userNotif.paginatedData);
  const reduxCurrentPage = useSelector((state) => state.userNotif.currentPage);
  const reduxLastPage = useSelector((state) => state.userNotif.lastPage);
  const reduxScrollPosition = useSelector((state) => state.userNotif.scrollPosition);
  const userData = useSelector((state) => state.account.detail);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const [notifData, setNotifData] = useState(reduxNotifData);
  const [currentPage, setCurrentPage] = useState(reduxCurrentPage);
  const [lastPage, setLastPage] = useState(reduxLastPage);
  const [isLoading, setIsLoading] = useState(false);
  const scrollContainerRef = useRef(null);

  // Modal states
  const [showAllModal, setShowAllModal] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [modalPage, setModalPage] = useState(1);
  const [modalTotalPages, setModalTotalPages] = useState(1);
  const [modalLoading, setModalLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [hoveredUnreadId, setHoveredUnreadId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch notifications on mount and when page changes
  const fetchNotifications = async (page = 1) => {
    try {
      setIsLoading(true);
      const resp = await getPaginatedNotif({ page, per_page: 10 });
      
      if (resp.success && resp.data && resp.data.notifications) {
        const notificationsData = resp.data.notifications;
        const newNotifications = notificationsData.data || [];
        const lastPageNum = notificationsData.last_page;
        
        if (!lastPageNum) {
          console.error('last_page not found in response');
          return;
        }
        
        let updatedData = newNotifications;
        if (page === 1) {
          setNotifData(newNotifications);
        } else {
          updatedData = [...notifData, ...newNotifications];
          setNotifData(updatedData);
        }
        
        // Update Redux store
        dispatch(storeUserPaginatedNotif(updatedData));
        dispatch(storeUserCurrentPage(page));
        dispatch(storeUserLastPage(lastPageNum));
        
        setLastPage(lastPageNum);
        setCurrentPage(page);
      } else {
        console.error('Invalid response structure:', resp);
        toast.error(t('http.request_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error(t('http.request_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch initial notifications on mount
  useEffect(() => {
    // Only fetch if notifData is empty (first time), otherwise load from Redux
    if (reduxNotifData.length === 0) {
      fetchNotifications(1);
    } else {
      setNotifData(reduxNotifData);
      setCurrentPage(reduxCurrentPage);
      setLastPage(reduxLastPage);
      // Restore scroll position after data is rendered
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = reduxScrollPosition;
        }
      }, 0);
    }
  }, []);

  // Handle scroll to load more
  const handleScroll = (e) => {
    const element = e.target;
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
    
    // Save scroll position to Redux
    dispatch(storeUserScrollPosition(element.scrollTop));
    
    if (isNearBottom && !isLoading && currentPage < lastPage) {
      fetchNotifications(currentPage + 1);
    }
  };

  const onClickNotif = async (notif, idx) => {
    const payload = { notifications: [notif.id] };
    const resp = await readNotification(payload);
    const isUnread = !notif.is_read;

    if (resp.success) {
      let newNotifData = [...notifData];
      newNotifData[idx].is_read = 1;
      setNotifData(newNotifData);
      dispatch(storeUserUnreadNotif(isUnread && notifUnread > 0 ? notifUnread - 1 : notifUnread));

      if (!notif.target_type) {
        return;
      }

      switch (notif.target_type) {
        case TARGET_TYPE_APPLICATION:
          navigate(`/applications/`);
        default:
          return;
      }
    } else {
      const errMsg = _get(resp, 'data.messages.0') || t('http.request_failed');
      toast.error(errMsg);
    }
  };

  // Fetch all notifications for modal
  const fetchAllNotifications = async (page = 1, perPage = itemsPerPage) => {
    try {
      setModalLoading(true);
      const resp = await getPaginatedNotif({ page, per_page: perPage });
      
      if (resp.success && resp.data && resp.data.notifications) {
        const notificationsData = resp.data.notifications;
        const notifs = notificationsData.data || [];
        const totalPages = notificationsData.last_page;
        
        // Always set to new data (replace, not accumulate)
        setAllNotifications(notifs);
        setModalPage(page);
        setModalTotalPages(totalPages);
        
        if (page === 1) {
          setShowAllModal(true);
        }
      } else {
        toast.error(t('http.request_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch all notifications:', error);
      toast.error(t('http.request_failed'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleSeeAllClick = (e) => {
    e.preventDefault();
    fetchAllNotifications(1, itemsPerPage);
  };

  const handleModalPageChange = (page) => {
    // Always fetch for any page change
    fetchAllNotifications(page, itemsPerPage);
  };

  const handleItemsPerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setItemsPerPage(newPerPage);
    // Reset to page 1 when changing items per page
    fetchAllNotifications(1, newPerPage);
  };

  const handleMarkAsRead = async (e, notif) => {
    e.stopPropagation();
    try {
      const payload = { notifications: [notif.id] };
      const resp = await readNotification(payload);
      
      if (resp.success) {
        // Update notification in modal table
        const updatedNotifications = allNotifications.map(n => 
          n.id === notif.id ? { ...n, is_read: 1 } : n
        );
        setAllNotifications(updatedNotifications);
        
        // Decrement unread count by 1 (only mark the one notification as read)
        dispatch(storeUserUnreadNotif(notifUnread > 0 ? notifUnread - 1 : 0));
        
        setHoveredUnreadId(null);
      } else {
        const errMsg = _get(resp, 'data.messages.0') || t('http.request_failed');
        toast.error(errMsg);
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
      toast.error(t('http.request_failed'));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const payload = { user_id: userData.id };
      const resp = await readAllNotification(payload);
      
      if (resp.success) {
        // Mark all notifications in current page as read
        const updatedNotifications = allNotifications.map(n => ({
          ...n,
          is_read: 1
        }));
        setAllNotifications(updatedNotifications);
        
        // Set unread count to 0
        dispatch(storeUserUnreadNotif(0));
        
        setHoveredUnreadId(null);
        setShowConfirmModal(false);
        toast.success('All notifications marked as read');
      } else {
        const errMsg = _get(resp, 'data.message') || t('http.request_failed');
        toast.error(errMsg);
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error(t('http.request_failed'));
    }
  };

  const getPageNumbersToShow = () => {
    const maxButtons = 5;
    const pages = [];
    
    if (modalTotalPages <= maxButtons) {
      // Show all pages if total pages is less than or equal to max buttons
      return Array.from({ length: modalTotalPages }, (_, i) => i + 1);
    }
    
    // Calculate start and end page numbers
    const halfButtons = Math.floor(maxButtons / 2);
    let startPage = modalPage - halfButtons;
    let endPage = modalPage + halfButtons;
    
    // Adjust if start is less than 1
    if (startPage < 1) {
      startPage = 1;
      endPage = maxButtons;
    }
    
    // Adjust if end exceeds total pages
    if (endPage > modalTotalPages) {
      endPage = modalTotalPages;
      startPage = modalTotalPages - maxButtons + 1;
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const getPaginatedModalNotifications = () => {
    // Since we're fetching per_page items, we just return the loaded data
    return allNotifications;
  };

  return (
    <Notification>
      <NotificationHeader>{t('menu.notifications')}</NotificationHeader>

      <NotificationBody ref={scrollContainerRef} onScroll={handleScroll}>
        {notifData.map((notif, idx) => (
          <NotificationItem key={`notif${idx}`} onClick={() => onClickNotif(notif, idx)}>
            <NotificationItemContent>
              {notif.content}
              {notif.created_at && (
                <NotificationItemTime>{DateToShortDateTime(notif.created_at, i18n.language)}</NotificationItemTime>
              )}
            </NotificationItemContent>
            <NotificationItemBadge show={!notif.is_read} />
          </NotificationItem>
        ))}
        {isLoading && <div style={{ textAlign: 'center', padding: '10px' }}>Loading...</div>}
      </NotificationBody>

      <NotificationFooter>
        <a href='#' onClick={handleSeeAllClick}>{t('see_all')}</a>
      </NotificationFooter>

      <NotificationModal
        show={showAllModal}
        onHide={() => {}}
        notifications={allNotifications}
        modalPage={modalPage}
        modalTotalPages={modalTotalPages}
        modalLoading={modalLoading}
        itemsPerPage={itemsPerPage}
        onPageChange={handleModalPageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onClose={() => setShowAllModal(false)}
        onMarkAllAsRead={() => setShowConfirmModal(true)}
        notifUnread={notifUnread}
        hoveredUnreadId={hoveredUnreadId}
        onHoveredUnreadIdChange={setHoveredUnreadId}
        onMarkAsRead={handleMarkAsRead}
        getPageNumbersToShow={getPageNumbersToShow}
        language={i18n.language}
      />

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirmation') || 'Confirmation'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('mark_all_as_read_confirm') || 'Are you sure you want to mark all notifications as read?'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button variant='primary' onClick={handleMarkAllAsRead}>
            {t('confirm') || 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Notification>
  );
};

export default UserNotification;
